import { AuthInfo } from '../contexts/AuthContext'
import { BlockReason, CourtType, BookingRecipient } from '../court/court'
import { PaymentMethod, PaymentStatus } from '../payment/payment'
import { DayOfWeek } from '../../utils/dateUtils'

/**
 * Types mirroring bounce-api spec-booking.yaml. Local date-times (club wall-clock time) are ISO strings without offset,
 * e.g. "2026-10-05T18:30:00"; instants (createdAt, expiresAt, serverTime) carry an offset.
 */

export type Money = {
  amount: number
  currency: string
}

export type TimeInterval = {
  startTime: string
  endTime: string
}

// ── Availability ─────────────────────────────────────────

export type AvailableStart = {
  startTime: string
  endTime: string
  price?: Money
}

export enum OccupiedType {
  BOOKING = 'BOOKING',
  /** A booking waiting for its online payment. */
  HOLD = 'HOLD',
  BLOCK = 'BLOCK',
}

/** An interval the court is occupied (without who occupies it); reason is set for blocks. */
export type OccupiedInterval = {
  startTime: string
  endTime: string
  type: OccupiedType
  reason?: BlockReason
}

export type CourtAvailability = {
  courtId: number
  courtName: string
  courtType: CourtType
  starts: AvailableStart[]
  /** Every interval the court is occupied (bookings, payment holds, blocks), soonest first. */
  occupied: OccupiedInterval[]
}

export type SessionAvailability = {
  durationMinutes: number
  courts: CourtAvailability[]
}

/** One day of a club, for every session length it offers (switching length needs no new request). */
export type ClubAvailability = {
  clubId: number
  date: string
  incrementMinutes: number
  sessions: SessionAvailability[]
}

/** The day's availability for the session length being picked. */
export type DayAvailability = SessionAvailability & {
  incrementMinutes: number
}

/** A session picked in the booking grid (not yet held). */
export type SelectedSession = {
  courtId: number
  courtName: string
  startTime: string
  endTime: string
  durationMinutes: number
  price?: Money
}

// ── Pricing ──────────────────────────────────────────────

export enum PriceRuleStatus {
  ACTIVE = 'ACTIVE',
  SUPERSEDED = 'SUPERSEDED',
  ARCHIVED = 'ARCHIVED',
}

export type PriceRuleAmount = {
  durationMinutes: number
  amount: number
}

export type PriceRule = {
  id?: number
  rootId?: number
  version?: number
  status?: PriceRuleStatus
  clubId: number
  name: string
  courtIds: number[]
  weekdays: DayOfWeek[]
  interval: TimeInterval
  effectiveFrom: string
  effectiveTo?: string | null
  currency: string
  amounts: PriceRuleAmount[]
}

// ── Passes ───────────────────────────────────────────────

export enum PassColor {
  DEFAULT = 'DEFAULT',
  DARK = 'DARK',
  ACCENT = 'ACCENT',
}

export type PassProduct = {
  id?: number
  versionId?: number
  version?: number
  /** When this version was created (ISO date-time); it applies to purchases from then on. */
  versionCreatedAt?: string
  /** Club the pass belongs to; every eligible court is one of its courts. */
  clubId?: number
  /** Read-only display names: the club, and the eligible courts in courtIds order. */
  clubName?: string
  courtNames?: string[]
  name: string
  color?: PassColor
  mostPopular?: boolean
  active?: boolean
  suspended?: boolean
  price: number
  currency: string
  includedMinutes: number
  validityDays: number
  /** Days ahead the holder may book, from the purchase day; empty keeps the global booking window. */
  bookAheadDays?: number | null
  courtIds: number[]
  weekdays: DayOfWeek[]
  intervals: TimeInterval[]
}

export enum PurchasedPassStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  VOID = 'VOID',
  /** All minutes spent and none can come back. */
  USED_UP = 'USED_UP',
}

export enum PurchasedPassSource {
  PURCHASE = 'PURCHASE',
  TOP_UP = 'TOP_UP',
}

export type PurchasedPass = {
  id: number
  name: string
  /** Club whose courts the pass can be used on. */
  clubId?: number | null
  color?: PassColor
  status: PurchasedPassStatus
  source: PurchasedPassSource
  totalMinutes: number
  remainingMinutes: number
  availableMinutes: number
  price?: Money
  purchasedAt: string
  expiresAt?: string | null
  /** Checkout the pass was bought in (used to return to a payment still in progress). */
  checkoutId?: string | null
  courtIds?: number[]
  weekdays?: DayOfWeek[]
  intervals?: TimeInterval[]
}

// ── Checkout (payment hold) ──────────────────────────────

export enum CheckoutType {
  BOOKING = 'BOOKING',
  PASS_PURCHASE = 'PASS_PURCHASE',
  RESCHEDULE = 'RESCHEDULE',
}

export enum CheckoutStatus {
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  RECONCILING = 'RECONCILING',
  CONFIRMED = 'CONFIRMED',
  EXPIRED = 'EXPIRED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  REFUND_PENDING = 'REFUND_PENDING',
  REFUNDED = 'REFUNDED',
  CANCELED = 'CANCELED',
}

/** Statuses in which the outcome is not decided yet. */
export const OPEN_CHECKOUT_STATUSES = [
  CheckoutStatus.PENDING_PAYMENT,
  CheckoutStatus.RECONCILING,
]

export enum RefundStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export type BookingItemRequest = {
  courtId: number
  startTime: string
  durationMinutes: number
}

export type CheckoutRequest = {
  clientRequestId: string
  type: CheckoutType
  items?: BookingItemRequest[]
  usePasses?: boolean
  topUpPassProductId?: number | null
  recipient?: BookingRecipient
  paymentMethod?: PaymentMethod
  /** Pay with the customer's refund points first (default true; management can switch it off). */
  useRefundPoints?: boolean
}

export type CheckoutQuoteItem = {
  courtId: number
  courtName: string
  startTime: string
  endTime: string
  durationMinutes: number
  price?: Money
  prepaidMinutes: number
  payableMinutes: number
}

export type CheckoutQuote = {
  items: CheckoutQuoteItem[]
  total: Money
  /** Part of the total paid with refund points. */
  pointsAmount: number
  /** What is left to pay by card or offline. */
  amountDue: Money
  prepaidMinutes: number
  missingMinutes: number
  topUpOptions: PassProduct[]
}

export type Refund = {
  status: RefundStatus
  amount: Money
  voided?: boolean
}

export type Checkout = {
  holdId: string
  type: CheckoutType
  status: CheckoutStatus
  createdAt?: string
  expiresAt?: string | null
  serverTime?: string
  paymentRequired: boolean
  paymentUrl?: string | null
  /** Paid with money (card or offline). */
  amount?: Money
  /** Refund points spent, on top of the amount. */
  pointsAmount?: number
  items: CheckoutQuoteItem[]
  passProductName?: string | null
  refund?: Refund
  /** True when a manager made the checkout on the customer's behalf. */
  madeOnBehalf: boolean
  /** Who made the checkout: the customer, or the manager who booked on their behalf. */
  createdByName?: string
  authInfo?: AuthInfo
}

// ── Bookings ─────────────────────────────────────────────

export enum BookingStatus {
  /** Held while its checkout waits for the online payment. */
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  CONFIRMED = 'CONFIRMED',
  CANCELED = 'CANCELED',
  RESCHEDULED = 'RESCHEDULED',
}

export enum BookingFunding {
  PAYMENT = 'PAYMENT',
  PASS = 'PASS',
  MIXED = 'MIXED',
  OFFLINE = 'OFFLINE',
  FREE = 'FREE',
}

export enum BookingFilter {
  ALL = 'ALL',
  UPCOMING = 'UPCOMING',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
}

export type Booking = {
  id: number
  clubId?: number
  clubName: string
  courtId?: number
  courtName: string
  courtType?: CourtType
  startTime: string
  endTime: string
  durationMinutes: number
  status: BookingStatus
  funding: BookingFunding
  price?: Money
  /** Part of the price paid with refund points. */
  pointsAmount?: number
  prepaidMinutes?: number
  paymentStatus?: PaymentStatus
  paymentMethod?: PaymentMethod
  rescheduleCount: number
  maxReschedules: number
  freeCancellationUntil?: string
  canceledAt?: string | null
  /** Checkout the booking was made in (to return to a payment still in progress). */
  checkoutId?: string | null
  /** Payment deadline of a PENDING_PAYMENT booking. */
  paymentExpiresAt?: string | null
  ownerName?: string
  ownerEmail?: string
  /** Who made the booking: the customer, or the manager who booked on their behalf (management views only). */
  createdByName?: string
}

export type CancellationResult = {
  bookingId: number
  restoredMinutes: number
  forfeitedMinutes: number
  /** Refund points returned for what was paid (1 point = 1 RON). */
  refundPoints: number
}

export type RescheduleRequest = {
  clientRequestId: string
  courtId: number
  startTime: string
  durationMinutes: number
}

export type CourtDeactivation = {
  futureBookings: 'KEEP' | 'CANCEL'
}

// ── Booking summary (UI) ─────────────────────────────────

/** One session in the booking summary, priced locally while picking and by the server quote on confirmation. */
export type SummaryLine = {
  key: string
  courtName: string
  startTime: string
  durationMinutes: number
  amount?: Money
  prepaidMinutes: number
}

/** Selection kept across navigation (confirmation page, profile completion, guest checkout steps). */
export type BookingDraft = {
  clubId?: number
  durationMinutes?: number
  day?: string
  sessions?: SelectedSession[]
  topUpPassProductId?: number
  recipient?: BookingRecipient
  paymentMethod?: PaymentMethod
  /** Manager booking: spend the customer's refund points. */
  useRefundPoints?: boolean
}

export type BookingLocationState = {
  bookingDraft?: BookingDraft
  resumeConfirmation?: boolean
}
